import {
  Calendar,
  Key,
  KeyCommand,
  Label,
  List,
  Medal,
  MediaImage,
  ProfileCircle,
  Shop,
  Flask,
  SystemRestart,
  LogIn,
} from "iconoir-react";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { useFeatureFlag } from "shared/contextProviders/featureFlags";
import { FeatureFlags } from "shared/featureFlags";
import { useOrgHome } from "apps/home/hooks";
import { EnumOrganizationMetadataDisabledApps } from "gql/generated";

export enum OrganizationSettingsViewKey {
  OrganizationProfile = "ORGANIZATION_PROFILE",
  TeamMembers = "TEAM_MEMBERS",
  DesignatedContacts = "DESIGNATED_CONTACTS", // Disabled for now
  Logo = "LOGO",
  EmailSettings = "EMAIL_SETTINGS",
  CustomTags = "CUSTOM_TAGS",
  MembershipTiers = "MEMBERSHIP_TIERS",
  Membership = "MEMBERSHIP", // Disabled for now
  StorefrontSettings = "STOREFRONT_SETTINGS",
  RegionDetails = "REGION_DETAILS", // Disabled for now
  License = "LICENSE",
  DirectorySettings = "DIRECTORY_SETTINGS",
  EventSettings = "EVENT_SETTINGS",
  WidgetSettings = "WIDGET_SETTINGS",
  UserSignUp = "USER_SIGN_UP",
  TestTools = "TEST_TOOLS",
}

export interface OrganizationSettingsViewProps {
  Icon: React.ForwardRefExoticComponent<
    Omit<React.SVGProps<SVGSVGElement>, "ref"> &
      React.RefAttributes<SVGSVGElement>
  >;
  title: string;
  description: string;
  key: OrganizationSettingsViewKey;
  /** When set, a section header with this title is rendered before this item */
  sectionTitle?: string;
}

const SETTINGS_SUBVIEW_KEY = "lastOrganizationSettingsSubView";

/** Legacy URL/session value before Account Setup was renamed to Storefront Settings */
const LEGACY_ACCOUNT_SETUP_VIEW = "ACCOUNT_SETUP";

const normalizeSettingsSubView = (
  raw: string | null,
): OrganizationSettingsViewKey | null => {
  if (!raw) return null;
  if (raw === LEGACY_ACCOUNT_SETUP_VIEW) {
    return OrganizationSettingsViewKey.StorefrontSettings;
  }
  if (raw === OrganizationSettingsViewKey.EmailSettings) {
    return OrganizationSettingsViewKey.Logo;
  }
  if (
    Object.values(OrganizationSettingsViewKey).includes(
      raw as OrganizationSettingsViewKey,
    )
  ) {
    return raw as OrganizationSettingsViewKey;
  }
  return null;
};

export const useOrganizationSettings = () => {
  const { t } = useTranslation("home", { keyPrefix: "VIEWS.SETTINGS.VIEWS" });
  const [searchParams, setSearchParams] = useSearchParams();
  const membershipTiersEnabled = useFeatureFlag(FeatureFlags.MembershipTiers);
  const testDataEnabled = useFeatureFlag(FeatureFlags.OnboardingTestDataTool);
  const { organization } = useOrgHome();
  const membershipLabel =
    organization?.metadata?.nomenclature?.membership || "Membership";
  const membershipTranslationValues = useMemo(
    () => ({
      membership: membershipLabel,
      membershipLower: membershipLabel.toLowerCase(),
    }),
    [membershipLabel],
  );
  const storefrontEnabled = !organization?.metadata?.disabledApps?.includes(
    EnumOrganizationMetadataDisabledApps.Storefront,
  );
  /** Org-level switch from Adminland: show Storefront settings even when the app is hidden for operators. */
  const orgStorefrontConnectionsAllowed =
    organization?.metadata?.storefrontOrgConnectionsEnabled !== false;
  const showStorefrontSettingsInNav =
    storefrontEnabled || orgStorefrontConnectionsAllowed;

  // Load the selected organization settings view from the session or URL (for deep links)
  const lastSelectedView = useMemo(() => {
    const fromUrl = normalizeSettingsSubView(
      searchParams.get("settingsSubView"),
    );
    if (fromUrl) {
      return fromUrl;
    }
    const fromSession = normalizeSettingsSubView(
      sessionStorage.getItem(SETTINGS_SUBVIEW_KEY),
    );
    return (
      fromSession ?? OrganizationSettingsViewKey.OrganizationProfile
    );
  }, [searchParams]);

  const views: OrganizationSettingsViewProps[] = useMemo(
    () =>
      [
        {
          key: OrganizationSettingsViewKey.OrganizationProfile,
          Icon: List,
          title: t(`${OrganizationSettingsViewKey.OrganizationProfile}.TITLE`),
          description: t(
            `${OrganizationSettingsViewKey.OrganizationProfile}.DESCRIPTION`,
          ),
        },
        {
          key: OrganizationSettingsViewKey.TeamMembers,
          Icon: ProfileCircle,
          title: t(`${OrganizationSettingsViewKey.TeamMembers}.TITLE`),
          description: t(
            `${OrganizationSettingsViewKey.TeamMembers}.DESCRIPTION`,
          ),
        },
        {
          key: OrganizationSettingsViewKey.Logo,
          Icon: MediaImage,
          title: t(`${OrganizationSettingsViewKey.Logo}.TITLE`),
          description: t(`${OrganizationSettingsViewKey.Logo}.DESCRIPTION`),
        },
        {
          key: OrganizationSettingsViewKey.CustomTags,
          Icon: Label,
          title: t(`${OrganizationSettingsViewKey.CustomTags}.TITLE`),
          description: t(
            `${OrganizationSettingsViewKey.CustomTags}.DESCRIPTION`,
          ),
        },
        {
          key: OrganizationSettingsViewKey.MembershipTiers,
          Icon: Medal,
          title: t(
            `${OrganizationSettingsViewKey.MembershipTiers}.TITLE`,
            membershipTranslationValues,
          ),
          description: t(
            `${OrganizationSettingsViewKey.MembershipTiers}.DESCRIPTION`,
            membershipTranslationValues,
          ),
        },
        {
          key: OrganizationSettingsViewKey.License,
          Icon: Key,
          title: t(`${OrganizationSettingsViewKey.License}.TITLE`),
          description: t(`${OrganizationSettingsViewKey.License}.DESCRIPTION`),
        },
        {
          key: OrganizationSettingsViewKey.DirectorySettings,
          Icon: KeyCommand,
          title: t(`${OrganizationSettingsViewKey.DirectorySettings}.TITLE`),
          description: t(
            `${OrganizationSettingsViewKey.DirectorySettings}.DESCRIPTION`,
          ),
          sectionTitle: t("APP_SETTINGS"),
        },
        {
          key: OrganizationSettingsViewKey.StorefrontSettings,
          Icon: Shop,
          title: t(`${OrganizationSettingsViewKey.StorefrontSettings}.TITLE`),
          description: t(
            `${OrganizationSettingsViewKey.StorefrontSettings}.DESCRIPTION`,
          ),
        },
        {
          key: OrganizationSettingsViewKey.EventSettings,
          Icon: Calendar,
          title: t(`${OrganizationSettingsViewKey.EventSettings}.TITLE`),
          description: t(
            `${OrganizationSettingsViewKey.EventSettings}.DESCRIPTION`,
          ),
        },
        {
          key: OrganizationSettingsViewKey.WidgetSettings,
          Icon: SystemRestart,
          title: t(`${OrganizationSettingsViewKey.WidgetSettings}.TITLE`),
          description: t(
            `${OrganizationSettingsViewKey.WidgetSettings}.DESCRIPTION`,
          ),
        },
        {
          key: OrganizationSettingsViewKey.UserSignUp,
          Icon: LogIn,
          title: t(`${OrganizationSettingsViewKey.UserSignUp}.TITLE`),
          description: t(
            `${OrganizationSettingsViewKey.UserSignUp}.DESCRIPTION`,
          ),
          sectionTitle: t("ONBOARDING_SECTION"),
        },
        {
          key: OrganizationSettingsViewKey.TestTools,
          Icon: Flask,
          title: t(`${OrganizationSettingsViewKey.TestTools}.TITLE`),
          description: t(`${OrganizationSettingsViewKey.TestTools}.DESCRIPTION`),
        },
      ].filter(
        view =>
          (import.meta.env.VITE_ENVIRONMENT !== "production" ||
            view.key !== OrganizationSettingsViewKey.License) &&
          (membershipTiersEnabled ||
            view.key !== OrganizationSettingsViewKey.MembershipTiers) &&
          (showStorefrontSettingsInNav ||
            view.key !== OrganizationSettingsViewKey.StorefrontSettings) &&
          (testDataEnabled || view.key !== OrganizationSettingsViewKey.TestTools),
      ),
    [
      t,
      membershipTiersEnabled,
      testDataEnabled,
      showStorefrontSettingsInNav,
      membershipTranslationValues,
    ],
  );

  const [selectedView, setSelectedView] =
    useState<OrganizationSettingsViewKey>(lastSelectedView);

  // Sync state when URL settingsSubView changes (e.g. browser back or direct link)
  useEffect(() => {
    const fromUrl = normalizeSettingsSubView(
      searchParams.get("settingsSubView"),
    );
    if (fromUrl) {
      setSelectedView(fromUrl);
    }
  }, [searchParams]);

  // Update the last selected view in session and URL so the link is shareable
  const handleSetSelectedView = (view: OrganizationSettingsViewKey) => {
    setSelectedView(view);
    sessionStorage.setItem(SETTINGS_SUBVIEW_KEY, view);
    const next = new URLSearchParams(searchParams);
    next.set("settingsSubView", view);
    setSearchParams(next, { replace: true });
  };

  return {
    views,
    selectedView,
    onSetSelectedView: handleSetSelectedView,
  };
};
